import React, { useEffect, useRef, useState } from 'react';
import isPlainObject from 'lodash/isPlainObject';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import BasicModalLayout from '../../../shared/components/modal/BasicModalLayout';
import { MATERIAL_ICON_DOWNLOAD, MATERIAL_ICON_LAUNCH } from '../../../shared/common/Button/Icon';
import {
    CampaignResult,
    CUMULATIVE_LOANS_KEY,
    CUMULATIVE_USERS_KEY,
    TOTAL_LOANS_KEY,
    TOTAL_LOANS_SHARED_KEY,
    VISITS_KEY,
    VisitSource
} from '../../../api/marketing/your-campaigns/campaignTypes';
import NoElementsFound, { NO_ELEMENTS_VISITS } from '../../../shared/components/NoElementsFound';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import {
    StAnalyticsContainer,
    StAnalyticsHeader,
    StAnalyticsModal,
    StBoldGray,
    StCenteredLinkText,
    StCenteredMaterialIcon,
    StCopiesContainer,
    StDescriptionContainer,
    StDiagramHeaderContainer,
    StDiagramLegendBlue,
    StDiagramLegendContainer,
    StDiagramLegendDescription,
    StDiagramLegendGrey,
    StDiagramTitle,
    StHSpacer,
    StVisitsLink,
    StVisitsTable,
    StVSpacer,
    StWarningValue
} from './analyticsModalStyled';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

interface Props {
    onClose: () => void;
    onDownloadAnalyticsPdf: () => void;
    visible: boolean;
    isInPartnerContext: boolean;
    title: string;
    authors: string;
    campaignStartDate: string;
    campaignEndDate: string;
    campaignTerm: number;
    campaignCopies: number;
    daysLeft: number;
    loansLeft: number;
    totalLoans: number;
    diagramData: CampaignResult;
}

function formatDate(date: string | number | Date, t: TFunction) {
    try {
        if (date) {
            return t('dateFormat.date', {
                val: new Date(date)
            });
        }
    } catch (e) {
        if (e instanceof TypeError) {
            return null;
        }
        throw e;
    }
}

export const AnalyticsModal = (props: Props) => {
    const { t } = useTranslation();
    const [bodyHeight, setBodyHeight] = useState<number | null>(null);
    const analyticsContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        const bodyHeight = document.body.getBoundingClientRect().height;
        setBodyHeight(bodyHeight);

        return () => {
            window.removeEventListener('resize', handleResize, false);
        };
    }, []);

    function handleResize() {
        const bodyHeight = document.body.getBoundingClientRect().height;
        setBodyHeight(bodyHeight);
    }

    const maxHeight = Math.floor((bodyHeight ?? 0) * 0.8 - 150);
    const {
        title,
        authors,
        campaignStartDate,
        campaignEndDate,
        daysLeft,
        campaignCopies,
        loansLeft,
        totalLoans,
        campaignTerm,
        diagramData
    } = props;

    const rowStyle = { fontSize: '14px' };
    const daysLeftNum = Math.max(daysLeft, 0);
    const LoansValueComponent = loansLeft <= totalLoans * 0.2 ? StWarningValue : 'strong';
    const DaysLeftValueComponent = daysLeftNum <= campaignTerm * 0.2 ? StWarningValue : 'strong';
    const daysLeftDescription =
        daysLeft < 0
            ? t('marketing.yourCampaigns.expired')
            : t('marketing.yourCampaigns.daysLeft', { count: daysLeft });
    const loansLeftDescription = t('marketing.yourCampaigns.loansLeftDescription', {
        loansLeft: loansLeft,
        totalLoans: totalLoans
    });

    if (!isPlainObject(diagramData) || Object.keys(diagramData).length < 4) {
        return null;
    }
    const inPartnerContext = props.isInPartnerContext;

    const cumulativeLoansRawSvg = diagramData[CUMULATIVE_LOANS_KEY]?.fileContent;
    const cumulativeTotalLoans = diagramData[CUMULATIVE_LOANS_KEY]?.totalLoans;
    const totalLoansRawSvg = diagramData[TOTAL_LOANS_KEY]?.fileContent;
    const averageLoansDuringCampaign = diagramData[TOTAL_LOANS_KEY]?.averageLoansDuringCampaign;
    const averageLoansPlus = diagramData[TOTAL_LOANS_KEY]?.averageLoansPlus;
    const usersRawSvg = diagramData[CUMULATIVE_USERS_KEY]?.fileContent;
    const totalUsers = diagramData[CUMULATIVE_USERS_KEY]?.totalRegisteredUsers;
    const totalUsersPlus = diagramData[CUMULATIVE_USERS_KEY]?.registeredUsersPlus;
    const totalUsersFormatted = t('numberFormat.numberWithoutDecimalPoint', { val: totalUsers });
    const totalUsersPlusFormatted = t('numberFormat.numberWithoutDecimalPoint', { val: totalUsersPlus });
    const cumulativeTotalLoansFormatted = t('numberFormat.numberWithoutDecimalPoint', { val: cumulativeTotalLoans });
    const sharedOnlyRawSvg = diagramData[TOTAL_LOANS_SHARED_KEY]?.fileContent ?? '';
    const sharedOnlyAverageLoansDuringCampaign = diagramData[TOTAL_LOANS_SHARED_KEY]?.averageLoansDuringCampaign;
    const sharedOnlyAverageLoansPlus = diagramData[TOTAL_LOANS_SHARED_KEY]?.averageLoansPlus;
    const sharedOnlyFormatted = t('numberFormat.numberWithoutDecimalPoint', {
        val: sharedOnlyAverageLoansDuringCampaign ?? 0
    });
    const sharedOnlyAverageLoansPlusFormatted = t('numberFormat.numberWithoutDecimalPoint', {
        val: sharedOnlyAverageLoansPlus ?? 0
    });
    const averageLoansDuringCampaignFormatted = t('numberFormat.numberWithoutDecimalPoint', {
        val: averageLoansDuringCampaign ?? 0
    });
    const averageLoansPlusFormatted = t('numberFormat.numberWithoutDecimalPoint', {
        val: averageLoansPlus ?? 0
    });

    let visitSources: VisitSource[] | null = null;
    if (isPlainObject(diagramData[VISITS_KEY])) {
        visitSources = diagramData[VISITS_KEY].sources;
    }

    const content = (
        <StAnalyticsModal height={maxHeight}>
            <StAnalyticsContainer ref={analyticsContainerRef}>
                <StDescriptionContainer>
                    <div>
                        {t('marketing.yourCampaigns.campaignPeriod')}
                        {'  '}
                        <strong>
                            {formatDate(campaignStartDate, t)} - {formatDate(campaignEndDate, t)}
                        </strong>
                        {'  '}
                        <DaysLeftValueComponent>{daysLeftDescription}</DaysLeftValueComponent>
                    </div>
                </StDescriptionContainer>
                <StDescriptionContainer>
                    <StCopiesContainer>
                        <div>
                            {t('marketing.yourCampaigns.campaignCopies')}
                            {'  '}
                            <strong>{campaignCopies}</strong>
                        </div>
                        <Button
                            icon={MATERIAL_ICON_DOWNLOAD}
                            onClick={props.onDownloadAnalyticsPdf}
                            size={ButtonSize.LARGE}
                            theme={ButtonTheme.NEUTRAL}
                            text={t('marketing.yourCampaigns.downloadAsPdf')}
                        />
                    </StCopiesContainer>
                </StDescriptionContainer>
                <StDescriptionContainer>
                    <div>
                        {t('marketing.yourCampaigns.loansLeft')}:{'  '}
                        <LoansValueComponent>{loansLeftDescription}</LoansValueComponent>
                    </div>
                </StDescriptionContainer>
            </StAnalyticsContainer>
            <StVSpacer factor={2} />
            <div id="cumulativeLoansContainer" className="diagram-container" data-testid="diagram-container">
                <StDiagramHeaderContainer>
                    <StDiagramTitle>{t('marketing.yourCampaigns.cumulativeLoans', { title: title })}</StDiagramTitle>
                    <StDiagramTitle>
                        {t('marketing.yourCampaigns.totalLoans')}: <strong>{cumulativeTotalLoansFormatted}</strong>
                    </StDiagramTitle>
                </StDiagramHeaderContainer>
                <div
                    id="cumulativeLoansDiagram"
                    data-testid="cumulativeLoansDiagram"
                    dangerouslySetInnerHTML={{ __html: cumulativeLoansRawSvg }}
                />
            </div>
            <StVSpacer factor={1} />
            {inPartnerContext ? (
                <div id="totalLoansSharedOnlyContainer" className="diagram-container" data-testid="diagram-container">
                    <StDiagramHeaderContainer>
                        <StDiagramTitle>
                            {t('marketing.yourCampaigns.totalLoansSharedOnlyContainerText')}
                        </StDiagramTitle>
                        <StDiagramTitle>
                            {t('marketing.yourCampaigns.averagePerDay')}: <strong>{sharedOnlyFormatted}</strong>{' '}
                            <StBoldGray>
                                ({sharedOnlyAverageLoansPlus && sharedOnlyAverageLoansPlus < 0 ? '' : '+'}
                                {sharedOnlyAverageLoansPlusFormatted})
                            </StBoldGray>
                        </StDiagramTitle>
                    </StDiagramHeaderContainer>
                    <div
                        id="cumulativeTotalLoansSharedOnlyDiagram"
                        dangerouslySetInnerHTML={{ __html: sharedOnlyRawSvg }}
                    />
                    <StDiagramLegendContainer>
                        <StDiagramLegendDescription>
                            <StDiagramLegendGrey /> {t('marketing.yourCampaigns.historical')}
                        </StDiagramLegendDescription>
                        <StHSpacer />
                        <StDiagramLegendDescription>
                            <StDiagramLegendBlue /> {t('marketing.yourCampaigns.campaignPeriod')}
                        </StDiagramLegendDescription>
                    </StDiagramLegendContainer>
                    <StVSpacer factor={1} />
                </div>
            ) : null}
            <div id="totalLoansContainer" className="diagram-container" data-testid="diagram-container">
                <StDiagramHeaderContainer>
                    <StDiagramTitle>
                        {inPartnerContext
                            ? t('marketing.yourCampaigns.totalLoansSharedAndIndividualContainerText')
                            : t('marketing.yourCampaigns.totalLoansCompleteCollectionContainerText')}
                    </StDiagramTitle>
                    <StDiagramTitle>
                        {t('marketing.yourCampaigns.averagePerDay')}:{' '}
                        <strong>{averageLoansDuringCampaignFormatted}</strong>{' '}
                        <StBoldGray>
                            ({averageLoansPlus < 0 ? '' : '+'}
                            {averageLoansPlusFormatted})
                        </StBoldGray>
                    </StDiagramTitle>
                </StDiagramHeaderContainer>
                <div
                    id="cumulativeTotalLoansDiagram"
                    data-testid="cumulativeTotalLoansDiagram"
                    dangerouslySetInnerHTML={{ __html: totalLoansRawSvg }}
                />
                <StDiagramLegendContainer>
                    <StDiagramLegendDescription>
                        <StDiagramLegendGrey /> {t('marketing.yourCampaigns.historical')}
                    </StDiagramLegendDescription>
                    <StHSpacer />
                    <StDiagramLegendDescription>
                        <StDiagramLegendBlue /> {t('marketing.yourCampaigns.campaignPeriod')}
                    </StDiagramLegendDescription>
                </StDiagramLegendContainer>
            </div>
            <StVSpacer factor={1} />
            <div id="totalUsersContainer" className="diagram-container" data-testid="diagram-container">
                <StDiagramHeaderContainer>
                    <StDiagramTitle>{t('marketing.yourCampaigns.totalUsersContainerText')}</StDiagramTitle>
                    <StDiagramTitle>
                        {t('marketing.yourCampaigns.totalRegisteredUsers')}: {}
                        <strong>{totalUsersFormatted}</strong>{' '}
                        <StBoldGray>
                            ({totalUsersPlus < 0 ? '' : '+'}
                            {totalUsersPlusFormatted})
                        </StBoldGray>
                    </StDiagramTitle>
                </StDiagramHeaderContainer>
                <div
                    id="totalUsersDiagram"
                    data-testid="totalUsersDiagram"
                    dangerouslySetInnerHTML={{ __html: usersRawSvg }}
                />
                <StDiagramLegendContainer>
                    <StDiagramLegendDescription>
                        <StDiagramLegendGrey /> {t('marketing.yourCampaigns.historical')}
                    </StDiagramLegendDescription>
                    <StHSpacer />
                    <StDiagramLegendDescription>
                        <StDiagramLegendBlue /> {t('marketing.yourCampaigns.campaignPeriod')}
                    </StDiagramLegendDescription>
                </StDiagramLegendContainer>
            </div>
            <StVSpacer factor={1} />
            {
                <div id="visitsContainer" data-testid="visitsContainer">
                    <StDiagramHeaderContainer>
                        <StDiagramTitle>
                            {t('marketing.yourCampaigns.visitsContainerText', { title: title })}
                        </StDiagramTitle>
                    </StDiagramHeaderContainer>
                    <StVisitsTable>
                        <thead>
                            <tr>
                                <th style={rowStyle}>{t('marketing.yourCampaigns.source')}</th>
                                <th style={rowStyle}>{t('marketing.yourCampaigns.sourcePath')}</th>
                                <th style={rowStyle}>{t('marketing.yourCampaigns.visits')}</th>
                            </tr>
                        </thead>
                        <tbody data-testid="tbody">
                            {!isArray(visitSources)
                                ? null
                                : visitSources?.map(visit => (
                                      <tr key={visit.host}>
                                          <td>{visit.host}</td>
                                          <td>
                                              {isEmpty(visit.uri) || isEmpty(visit.path) ? null : (
                                                  <StVisitsLink
                                                      href={visit.uri}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                  >
                                                      <StCenteredLinkText>{visit.path}</StCenteredLinkText>
                                                      <StCenteredMaterialIcon icon={MATERIAL_ICON_LAUNCH} />
                                                  </StVisitsLink>
                                              )}
                                          </td>
                                          <td>{`${t('numberFormat.numberWithoutDecimalPoint', {
                                              val: visit.views.number
                                          })} (${visit.views.percentage}%)`}</td>
                                      </tr>
                                  ))}
                        </tbody>
                    </StVisitsTable>
                    {visitSources?.length === 0 ? <NoElementsFound type={NO_ELEMENTS_VISITS} margin={10} /> : null}
                </div>
            }
        </StAnalyticsModal>
    );

    return (
        <BasicModalLayout
            header={
                <StAnalyticsHeader>
                    {t('marketing.yourCampaigns.titleByAuthors', { title: title, authors: authors })}
                </StAnalyticsHeader>
            }
            content={content}
            cancelButton={undefined}
            okButton={undefined}
            visible={props.visible}
            width={1024}
            onClose={props.onClose}
        />
    );
};
